import { inject, Injectable, signal } from "@angular/core";

import { Observable } from "rxjs";

import { SendDialogComponent } from "@components/index";
import { OverlayCommonService } from "@components/shared/overlay/overlay-common.service";
import { ProcessInputOutputTypeEnum, SendTargetTypeEnum } from "@shared/enums";
import {
  IItemExtended,
  IProcessInput,
  IProcessOutput,
  IProductExtended,
  ProcessInputOutputType,
} from "@shared/interfaces";
import { ItemsService, ProcessesService, ProductsService } from "@shared/services";
import { CommonUtils } from "@shared/utils";

@Injectable({
  providedIn: "root",
})
export class ProcessOverlayService extends OverlayCommonService {
  private readonly productsService: ProductsService = inject(ProductsService);

  private readonly processesService: ProcessesService = inject(ProcessesService);

  private readonly itemsService: ItemsService = inject(ItemsService);

  public inputCounter = signal<number>(0);

  public outputCounter = signal<number>(0);

  public allProducts = signal<IProductExtended[]>([]);

  // Signals to store inputs and outputs data
  public processInputs = signal<IProcessInput[]>([]);

  public processOutputs = signal<IProcessOutput[]>([]);

  // Signals to store related items
  public processInputItems = signal<IItemExtended[]>([]);

  public processOutputItems = signal<IItemExtended[]>([]);

  // Signals to indicate if data is loading
  public isLoadingInputs = signal<boolean>(false);

  public isLoadingOutputs = signal<boolean>(false);

  constructor() {
    super();
  }

  public async onReloadProducts(includes = []): Promise<void> {
    try {
      const allProducts = await this.productsService.getAllGraphQL(undefined, undefined, includes);

      this.allProducts.set(allProducts);
    } catch (error) {
      this.notificationService.showError(error);
    }
  }

  async loadAllProcessData(processId: string, forceReload: boolean = false): Promise<void> {
    await Promise.all([
      this.loadProcessData(processId, ProcessInputOutputTypeEnum.INPUT, forceReload),
      this.loadProcessData(processId, ProcessInputOutputTypeEnum.OUTPUT, forceReload),
    ]);
  }

  async refreshProcessData(processId: string, type?: ProcessInputOutputType): Promise<void> {
    if (!type) {
      await Promise.all([
        this.loadProcessData(processId, ProcessInputOutputTypeEnum.INPUT, true),
        this.loadProcessData(processId, ProcessInputOutputTypeEnum.OUTPUT, true),
      ]);
    } else if (type === ProcessInputOutputTypeEnum.INPUT) {
      await this.loadProcessData(processId, ProcessInputOutputTypeEnum.INPUT, true);
    } else if (type === ProcessInputOutputTypeEnum.OUTPUT) {
      await this.loadProcessData(processId, ProcessInputOutputTypeEnum.OUTPUT, true);
    }
  }

  async loadProcessData(
    processId: string,
    type: ProcessInputOutputType,
    forceReload: boolean = false,
  ): Promise<void> {
    if (!forceReload) {
      if (type === ProcessInputOutputTypeEnum.INPUT && this.processInputs().length > 0) {
        //Inputs already loaded
        return;
      } else if (type === ProcessInputOutputTypeEnum.OUTPUT && this.processOutputs().length > 0) {
        //Outputs already loaded;
        return;
      }
    }

    // Set loading state
    if (type === ProcessInputOutputTypeEnum.INPUT) {
      this.isLoadingInputs.set(true);
    } else {
      this.isLoadingOutputs.set(true);
    }

    try {
      // Get elements (inputs or outputs)
      const response =
        type === ProcessInputOutputTypeEnum.INPUT
          ? await this.processesService.getManyInputs(processId)
          : await this.processesService.getManyOutputs(processId);

      const selectedElements = response.content;

      // Extract item IDs and create quantity map
      const itemIds: string[] = [];
      const itemQuantityMap = new Map<string, number>();

      for (const element of selectedElements) {
        for (const elementItem of element.items) {
          const itemId = CommonUtils.getUriId(elementItem.item);

          itemIds.push(itemId);

          if (type === ProcessInputOutputTypeEnum.INPUT) {
            itemQuantityMap.set(itemId, elementItem.selectedQuantity);
          }
        }
      }

      // Get related items
      let processItems: IItemExtended[] = [];

      if (itemIds.length > 0) {
        const items = await this.itemsService.getByIdsGraphQL(itemIds);

        processItems = items.map((item) => {
          const quantity =
            type === ProcessInputOutputTypeEnum.INPUT
              ? itemQuantityMap.get(item.id)
              : item.initialQuantity;

          return { ...item, selectedQuantity: quantity };
        });
      }

      // Update signals with obtained data
      if (type === ProcessInputOutputTypeEnum.INPUT) {
        this.processInputs.set(selectedElements);
        this.processInputItems.set(processItems);
        this.inputCounter.set(selectedElements.length);
      } else {
        this.processOutputs.set(selectedElements);
        this.processOutputItems.set(processItems);
        this.outputCounter.set(selectedElements.length);
      }
    } catch (error) {
      this.notificationService.showError(error);
    } finally {
      // Set loading finished
      if (type === ProcessInputOutputTypeEnum.INPUT) {
        this.isLoadingInputs.set(false);
      } else {
        this.isLoadingOutputs.set(false);
      }
    }
  }

  override setCountersToLoadingState() {
    super.setCountersToLoadingState();
    this.inputCounter.set(null);
    this.outputCounter.set(null);
    this.isLoadingInputs.set(true);
    this.isLoadingOutputs.set(true);
  }

  override setCountersEmptyState() {
    super.setCountersEmptyState();
    this.inputCounter.set(0);
    this.outputCounter.set(0);
    this.processInputs.set([]);
    this.processOutputs.set([]);
    this.processInputItems.set([]);
    this.processOutputItems.set([]);
    this.isLoadingInputs.set(false);
    this.isLoadingOutputs.set(false);
  }

  public send(activeOrganisationId: string, processId: string): Observable<{ hasSaved: boolean }> {
    return this.dialog
      .open(SendDialogComponent, {
        data: {
          sendTargetType: SendTargetTypeEnum.PROCESS,
          rootRecordUri: `/organisations/${activeOrganisationId}/processes/${processId}`,
        },
      })
      .afterClosed();
  }
}
